// CodeMirror-flavoured wrapper around `@nowline/lsp-worker`'s
// `NowlineLanguageClient`. The OSS package deliberately ships only the
// transport + LSP request/response surface — building the
// `@codemirror/lint` / `@codemirror/autocomplete` / `@codemirror/view`
// extensions on top is downstream wiring (m4.7 handoff). This module is
// the home for that wiring; the five extension factories in
// `./extensions/` are the first CodeMirror consumer of the OSS client,
// and their API shape is what `apps/free` and `apps/pro` both build on.
//
// Responsibilities:
//
//   - Spawn the worker (via `./worker.ts`) and complete the LSP
//     `initialize` handshake before resolving.
//   - Track the document version monotonically. CodeMirror's transaction
//     stream is the source of truth; `attach(view)` returns a ViewPlugin
//     that walks each transaction's `ChangeSet` and emits incremental
//     `textDocument/didChange` notifications.
//   - Convert CodeMirror positions to LSP `Position` objects on every
//     request so the extension factories don't repeat the boilerplate.
//   - Fan out `publishDiagnostics` to internal listeners (the lint
//     extension subscribes; `EditorPane` subscribes via Editor.tsx's
//     `onDiagnostics` callback to forward rows to PreviewPane).

import type { EditorState, Extension } from '@codemirror/state';
import type { EditorView, ViewUpdate } from '@codemirror/view';
import { ViewPlugin } from '@codemirror/view';
import { createNowlineLanguageClient, type NowlineLanguageClient } from '@nowline/lsp-worker';
import type {
    CompletionItem,
    Definition,
    Hover,
    Location,
    PublishDiagnosticsParams,
    TextDocumentContentChangeEvent,
} from 'vscode-languageserver-protocol';
import { cmPosToLspPosition } from './positions.ts';
import { spawnLspWorker } from './worker.ts';

export interface CreateLspClientOptions {
    /**
     * Synthetic URI for the editor's document. Defaults to
     * `memory:///nowline-free.nowline`. The LSP server normalises this
     * via `URI.parse`, so both three-slash and single-slash forms are
     * accepted in subsequent requests; for symmetry, callers should
     * stick with the URI returned from `client.uri`.
     */
    uri?: string;
    /** LSP `languageId` advertised on `didOpen`. Defaults to `nowline`. */
    languageId?: string;
}

export interface LspClient {
    /** The URI used for every LSP request/notification this client emits. */
    readonly uri: string;
    /**
     * Send `textDocument/didOpen` with the view's current document and
     * return a CodeMirror `Extension` that keeps the server in sync via
     * incremental `didChange` notifications. Call exactly once per
     * `LspClient` instance, immediately before the returned extension
     * is reconfigured into the view's compartment.
     *
     * The returned extension is a ViewPlugin; it walks each transaction's
     * `ChangeSet` and translates the changes into LSP
     * `TextDocumentContentChangeEvent` deltas. Multi-change transactions
     * are sent in reverse position order so each delta's range remains
     * valid in the document state immediately before it is applied —
     * the LSP server applies them sequentially, and CodeMirror reports
     * all change coordinates against the pre-transaction document.
     */
    attach(view: EditorView): Extension;
    /** LSP `textDocument/completion`. The position is computed from `state.doc`. */
    completion(state: EditorState, pos: number): Promise<CompletionItem[]>;
    /** LSP `textDocument/hover`. */
    hover(state: EditorState, pos: number): Promise<Hover | null>;
    /** LSP `textDocument/definition`. */
    definition(state: EditorState, pos: number): Promise<Definition | null>;
    /** LSP `textDocument/references` with `includeDeclaration: true`. */
    references(state: EditorState, pos: number): Promise<Location[]>;
    /** Subscribe to `textDocument/publishDiagnostics`. Returns a disposer. */
    onDiagnostics(listener: (params: PublishDiagnosticsParams) => void): { dispose(): void };
    /**
     * Close the document, shut the server down, and terminate the
     * worker. Safe to call multiple times — subsequent calls are no-ops.
     */
    dispose(): Promise<void>;
}

export async function createLspClient(options: CreateLspClientOptions = {}): Promise<LspClient> {
    const uri = options.uri ?? 'memory:///nowline-free.nowline';
    const languageId = options.languageId ?? 'nowline';
    const worker = spawnLspWorker();

    // createNowlineLanguageClient resolves only after the `initialize`
    // handshake completes and the server reports
    // `textDocumentSync: Incremental`. If the server ever regresses on
    // that capability the promise rejects with a descriptive error
    // here, which surfaces as a startup failure rather than mysterious
    // didChange drops downstream.
    let inner: NowlineLanguageClient;
    try {
        inner = await createNowlineLanguageClient({ worker });
    } catch (err) {
        worker.terminate();
        throw err;
    }

    let version = 0;
    let attached = false;
    let disposed = false;

    return {
        get uri() {
            return uri;
        },
        attach(view) {
            if (attached) {
                throw new Error(
                    'LspClient.attach() called more than once. Construct a fresh client per editor instance.',
                );
            }
            attached = true;
            version = 1;
            inner.didOpen({
                uri,
                languageId,
                version,
                text: view.state.doc.toString(),
            });
            const ownerUri = uri;
            return ViewPlugin.fromClass(
                class {
                    update(update: ViewUpdate): void {
                        if (disposed || !update.docChanged) return;
                        const changes: TextDocumentContentChangeEvent[] = [];
                        update.changes.iterChanges((fromA, toA, _fromB, _toB, insert) => {
                            const start = cmPosToLspPosition(update.startState.doc, fromA);
                            const end = cmPosToLspPosition(update.startState.doc, toA);
                            changes.push({ range: { start, end }, text: insert.toString() });
                        });
                        if (!changes.length) return;
                        version += 1;
                        // Apply highest-position changes first so each
                        // remaining delta's range stays valid in the
                        // doc state immediately before it is applied
                        // (the LSP server processes the array left to
                        // right, mutating the document between entries).
                        changes.reverse();
                        inner.didChange({ uri: ownerUri, version, changes });
                    }
                },
            );
        },
        async completion(state, pos) {
            return await inner.completion(uri, cmPosToLspPosition(state.doc, pos));
        },
        async hover(state, pos) {
            return await inner.hover(uri, cmPosToLspPosition(state.doc, pos));
        },
        async definition(state, pos) {
            return await inner.definition(uri, cmPosToLspPosition(state.doc, pos));
        },
        async references(state, pos) {
            return await inner.references(uri, cmPosToLspPosition(state.doc, pos));
        },
        onDiagnostics(listener) {
            return inner.onDiagnostics(listener);
        },
        async dispose() {
            if (disposed) return;
            disposed = true;
            if (attached) {
                try {
                    inner.didClose(uri);
                } catch {
                    // The connection may have already closed (e.g. the
                    // worker crashed). dispose() below still runs.
                }
            }
            try {
                await inner.dispose();
            } finally {
                worker.terminate();
            }
        },
    };
}
